'use client'

import { HOUSE_LIST_VIEWED_IDS_KEY, HOUSE_TYPE_ID, LAST_ESTIMATE_MAP_SLUG_KEY } from '@config'
import useFetch from '@hooks/useFetch'
import getEstimateRoute from '@plugin/searchRoute/modules/estimate'
import { suburbsToIds } from '@plugin/searchRoute/utils'
import { buildUrl } from '@plugin/http/lib/utils'
import { combinePoints, joinStr, toNumber } from '@utils'
import { assign, concat, filter, find, get, map, omit, size, split } from 'lodash-es'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useBoolean, useList, useLocalStorage, useMount } from 'react-use'
import { useSearchParams } from 'next/navigation'

export const EstimateMapContext = createContext()
export function useEstimateMapContext() {
  return useContext(EstimateMapContext)
}

export default function EstimateMapContextProvider({
  state,
  children
}) {

  const searchParams = useSearchParamsJSON()
  const coor = useCoor(state)
  const [houseId, setHouseId] = useState(state.houseId)
  useMount(() => {
    const d = localStorage.getItem('map-listing-click')
    if (d) {
      localStorage.removeItem('map-listing-click')
      setHouseId(toNumber(get(JSON.parse(d), 'id')))
    }
  })
  // 默认显示houseId对应卡片
  const [displayHouseCard, toggleDisplayCard] = useBoolean(!!houseId)

  // 地图范围
  const [bounds, setBounds] = useState()
  const [rect, setRect] = useState()
  const [zoom, setZoom] = useState()

  // 当前地图渲染的marker
  const [markerList, { reset: resetMarkerList, push: pushMarker }] = useList([])
  // 鼠标当前悬浮的房源详情数据
  const [hoverDetailData, setHoverDetailData] = useState()

  // 地图上点击过的marker ids
  const [viewedIds, setViewedIds] = useLocalStorage(HOUSE_LIST_VIEWED_IDS_KEY, [])
  // map data
  const mapDataState = useMapData({ bounds, rect, zoom }, displayHouseCard ? houseId : undefined, resetMarkerList)
  // street data
  const streetData = useStreet(state)

  // 地图数据加载
  const mapLoadData = useCallback(map => {
    const bounds = map.getBounds()
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    setRect([sw.lng, ne.lng < 0 ? 180 : ne.lng, sw.lat, ne.lat])
    setBounds(bounds)
    setZoom(map.getZoom())
  }, [setBounds, setRect, setZoom])

  // 添加已查看id
  const pushViewedId = useCallback(id => {
    // 暂时屏蔽已查看功能
    // if (viewedIds.includes(toNumber(id))) return
    // viewedIds.push(toNumber(id))
    // setViewedIds([...viewedIds])
    // }, [setViewedIds, viewedIds])
  }, [])

  // 缓存最后一次搜索的slug
  const [, setLastEstimateSlug] = useLocalStorage(LAST_ESTIMATE_MAP_SLUG_KEY)
  useEffect(() => {
    setLastEstimateSlug({
      slug: state.slug,
      searchParams
    })
  }, [state, searchParams, setLastEstimateSlug])
  return (
    <EstimateMapContext.Provider value={{
      ...mapDataState,
      streetData,
      state,
      coor,
      houseId,
      viewedIds,
      hoverDetailData,
      markerList,
      displayHouseCard,
      mapLoadData,
      pushViewedId,
      setHoverDetailData,
      resetMarkerList,
      pushMarker,
      toggleDisplayCard
    }}>
      {children}
    </EstimateMapContext.Provider>
  )
}

function useMapData({
  bounds,
  rect,
  zoom
}, houseId, resetMarkerList) {

  // show for sale
  const [showSale, toggleSale] = useLocalStorage('estimate-map-for-sale', true)
  // show for rent
  const [showRent, toggleRent] = useLocalStorage('estimate-map-for-rent', false)
  // show sold
  const [showSold, toggleSold] = useLocalStorage('estimate-map-sold', false)
  // show estimate
  const [showEstimate, toggleEstimate] = useBoolean(true)
  // show RV
  // const [showRV, toggleRV] = useBoolean(true)

  // 地图数据: for sale
  const [saleData, saleLoading] = useTypeData({ rect, bounds, typeId: HOUSE_TYPE_ID.residential, visible: showSale })
  // 地图数据: for rent
  const [rentData, rentLoading] = useTypeData({ rect, bounds, typeId: HOUSE_TYPE_ID.rent, visible: showRent })
  // 地图数据: sold
  const [soldData, soldLoading] = useTypeData({ rect, bounds, typeId: HOUSE_TYPE_ID.sold, visible: showSold })
  // 地图数据: estimate RV
  const [propertyData, propertyLoading] = usePropertyData({ rect, zoom, bounds, showEstimate, /*showRV */ }, houseId, resetMarkerList)

  // estimate数据需要和forsale数据合并
  // estimate数据如果在forsale中出现，则不显示这个estimate数据
  const combinePropertyData = useMemo(() => {
    let res = propertyData
    if (size(saleData) > 0) {
      res = filter(propertyData, item => {
        const houseId = toNumber(item.houseId)
        return !find(saleData, jtem => (toNumber(jtem.id) === houseId))
      })
    }
    if (size(soldData) > 0) {
      res = filter(propertyData, item => {
        const houseId = toNumber(item.id)
        return !find(soldData, jtem => (toNumber(jtem.propertyId) === houseId))
      })
    }
    return res
  }, [saleData, soldData, propertyData])

  return {
    salePoints: combinePoints(saleData),
    rentPoints: combinePoints(rentData),
    soldPoints: combinePoints(soldData),
    propertyPoints: combinePoints(combinePropertyData),
    isLoading: propertyLoading || saleLoading || rentLoading || soldLoading,
    showSale,
    showRent,
    showSold,
    showEstimate,
    // showRV,
    toggleSale,
    toggleRent,
    toggleSold,
    toggleEstimate,
    // toggleRV
  }
}

function useTypeData({
  rect,
  bounds,
  typeId,
  visible,
}) {

  const [mapData, setMapData] = useState([])
  const params = useMemo(() => {
    if (!rect) return
    const rIds = map(filter(mapData, item => bounds.contains([item.lng, item.lat])), item => item.id)
    return assign(
      { rect, getPoint: 1 },
      size(rIds) ? { rIds } : undefined,
      typeId ? { typeId } : undefined,
    )
  }, [rect, bounds, mapData, typeId])
  const { data, isLoading } = useFetch(
    visible && params
      ? { url: 'houses/list', params, fetchKey: buildUrl('houses/list', undefined, omit(params, 'rIds')) }
      : null,
    {
      keepPreviousData: true
    }
  )
  useEffect(() => setMapData(data), [data, setMapData])

  return [
    visible ? data : [],
    isLoading
  ]
}

function usePropertyData({
  rect,
  zoom,
  bounds,
  showSold = false,
  showEstimate = false,
  // showRV = false
}, houseId, resetMarkerList) {

  const [mapData, setMapData] = useState([])
  const visible = showSold || showEstimate /**|| showRV */
  const params = useMemo(() => {
    if (!rect) return
    const rIds = map(filter(mapData, item => bounds.contains([item.lng, item.lat])), item => item.id)
    if (houseId) rIds.push(houseId)
    const mapTypeIds = joinStr([
      showSold ? HOUSE_TYPE_ID.sold : void 0,
      showEstimate ? HOUSE_TYPE_ID.estimate : void 0,
      showEstimate ? HOUSE_TYPE_ID.rv : void 0,
      // showRV ? HOUSE_TYPE_ID.rv : void 0,
    ], ',')
    return assign(
      { rect, zoom: Math.floor(zoom) },
      getDensity(zoom),
      size(rIds) ? { rIds } : undefined,
      mapTypeIds ? { mapTypeIds } : undefined
    )
  }, [rect, zoom, bounds, mapData, houseId, showSold, showEstimate/**, showRV */])
  const { data, isLoading } = useFetch(
    visible && params
      ? { url: 'property-map', params, fetchKey: buildUrl('property-map', undefined, omit(params, 'rIds')) }
      : null,
    {
      keepPreviousData: true,
      onSuccess() {
        resetMarkerList()
      }
    }
  )
  useEffect(() => setMapData(data), [data, setMapData])

  return [
    visible ? data : [],
    isLoading
  ]
}

function useStreet(state) {
  const estimateRoute = getEstimateRoute()
  estimateRoute.fromSlug(state.slug)
  const { streetId } = suburbsToIds(estimateRoute.street).idStr()
  const { data } = useFetch(streetId ? `street/${streetId}` : undefined)
  return data
}

function getDensity(zoom) {
  let dimension = 8
  let density = 5
  return { dimension, density }
}

function useCoor(state) {
  let coor
  const searchParams = useSearchParamsJSON()
  const { slug } = state
  const { center, zoom: routeZoom } = searchParams

  let params
  if (center && routeZoom) {
    const lnglat = map(split(decodeURIComponent(center), ','), item => toNumber(item))
    const zoom = toNumber(routeZoom)
    coor = { lng: get(lnglat, 0), lat: get(lnglat, 1), zoom }
  } else {
    const estimateRoute = getEstimateRoute()
    estimateRoute.fromSlug(slug)
    params = suburbsToIds(concat(estimateRoute.region, estimateRoute.district, estimateRoute.suburb, estimateRoute.street)).idStr()
  }

  const { data } = useFetch(params ? { url: 'enum/zoom-n-center', params } : null)
  if (data) {
    const lat = toNumber(get(data, 'coor.lat'))
    const lng = toNumber(get(data, 'coor.lng'))
    const zoom = toNumber(get(data, 'zoom'))
    coor = { lng, lat, zoom }
  }

  return coor
}

function useSearchParamsJSON() {
  const searchParams = useSearchParams()
  const query = useMemo(() => {
    const res = {}
    searchParams.forEach((val, key) => {
      res[key] = decodeURIComponent(val)
    })
    return res
  }, [searchParams])

  return query
}